// ═══════════════════════════════════════════════════════════════════
// MarkChipSocial.js : THE MARK CHIP WITH FRIENDS INSIDE  (caP'n, 2026-10-25)
// ───────────────────────────────────────────────────────────────────
// MASTER COPY: gamemaster/engine/MarkChipSocial.js. Vendored byte-identical, with Mark.js, IEFriends.js, IEGlyph.js
// and ieGlyphs.js, into the titles that use it (Tilt and Rush first).
//
// caP'n's rulings (plan 7c-3): the Mark chip is on menus, lobbies and calm moments and hidden where it would crowd
// intense play; and there is NO separate friends pill. Friends live inside the chip.
//
//   <MarkFriendsChip uid onOpenMark/>   your nameplate (click: your Mark) with a small heart segment attached:
//                                       online friends, and a badge for requests waiting. Click the heart: friends panel.
//   <SocialDock game accent MarkView/>  the ordinary friends dock with its pill turned off, opened from the chip.
//
// Hiding mid-play is NOT done here: the title already calls setSocialHidden(true) while a race/table is live (the same signal
// that hides the dock), and the title's corner hides itself on useSocialHidden().
// The heart is drawn in code (IEGlyph): no image host, no Firebase.
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useEffect } from "react";
import IEGlyph from "./IEGlyph";
import IESocialDock from "./IEFriends";
import { MarkChip } from "./Mark";
import { useSocialBridge } from "./ieSocial";
import { MK, mkCut } from "./markData";

// ── one open/closed flag the chip and the dock share (module-level: the dock is mounted beside <App/>, not inside it) ──
let _open = false;
const _subs = new Set();
export function setFriendsOpen(v) {
  const n = !!v;
  if (n === _open) return;
  _open = n;
  _subs.forEach((f) => { try { f(n); } catch (e) {} });
}
export function useFriendsOpen() {
  const [o, setO] = useState(_open);
  useEffect(() => { _subs.add(setO); setO(_open); return () => { _subs.delete(setO); }; }, []);
  return o;
}

// the friends dock, pill off; the chip below is its door
export function SocialDock({ game, accent, zone = null, MarkView = null }) {
  const open = useFriendsOpen();
  return <IESocialDock game={game} accent={accent} zone={zone} MarkView={MarkView}
                       hidePill open={open} onOpenChange={setFriendsOpen} />;
}

export default function MarkFriendsChip({ uid, dense = true, onOpenMark }) {
  const social = useSocialBridge();
  const open = useFriendsOpen();
  const list = (social && social.friends) || [];
  const online = list.filter((f) => f && f.online).length;
  const asks = social && social.requestsIn ? social.requestsIn.length : 0;
  const shown = asks > 0 ? asks : (online > 0 ? online : list.length);
  return (
    <span style={{ display: "inline-flex", alignItems: "stretch", gap: 5 }}>
      <MarkChip uid={uid} dense={dense} onOpen={onOpenMark} />
      {social && (
        <button
          type="button"
          onClick={() => setFriendsOpen(!open)}
          title={asks > 0 ? `${asks} friend request${asks === 1 ? "" : "s"}` : online > 0 ? `${online} friend${online === 1 ? "" : "s"} online` : "Friends"}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer",
            padding: dense ? "3px 8px" : "5px 10px",
            background: open ? MK.slabHi : MK.obsidian, border: `1px solid ${asks > 0 ? MK.verdP : MK.edge}`,
            clipPath: mkCut(7), color: MK.bone, fontFamily: MK.mono, fontSize: dense ? 10.5 : 12,
          }}
        >
          <IEGlyph kind="heart" size={16} />
          {shown > 0 && (
            <span style={{ color: asks > 0 ? MK.verdP : online > 0 ? MK.bone : MK.smokeP, letterSpacing: ".06em" }}>
              {asks > 0 ? `+${asks}` : shown}
            </span>
          )}
        </button>
      )}
    </span>
  );
}
